import {
  trackSelfDescribingEvent,
  newTracker,
  type BrowserTracker,
} from "@snowplow/browser-tracker";
import { localStorageAccessible } from "@snowplow/browser-tracker-core";

interface Event {
  name: string;
  data: Record<string, unknown>;
  schema?: string;
}

const GENERIC_SCHEMA = "iglu:io.greenhouse/generic-event/jsonschema/1-0-0";

export class SnowplowTracker {
  private tracker: BrowserTracker | null | undefined = null;

  track(event: Event) {
    try {
      if (!this.enabled) {
        return;
      }

      this.lazyInit();

      this.logEvent(event);

      trackSelfDescribingEvent({
        event: {
          schema: event.schema || GENERIC_SCHEMA,
          data: {
            ...event.data,
            event_name: event.name,
          },
        },
      });
    } catch (e: unknown) {
      // Initializing the tracker requires crypto.randomUUID to be available.
      // It isn't in older browsers, and the fallback provided by uuid doesn't seem to work.
      // This is a workaround to avoid logging rollbars in this scenario.
      if (e instanceof TypeError && e.message?.includes("randomUUID")) {
        return;
      }
    }
  }

  // Lazily initialize the tracker to avoid trying to access window.ENV during SSR
  private lazyInit() {
    if (this.initialized) {
      return;
    }

    this.tracker = newTracker(window.ENV.SNOWPLOW_NAMESPACE, window.ENV.SNOWPLOW_ENDPOINT, {
      appId: window.ENV.SNOWPLOW_APP_ID,
      platform: "web",
      respectDoNotTrack: true,
      bufferSize: 1,
      encodeBase64: false,
      eventMethod: "post",
      keepalive: true,
      cookieSameSite: "None",
      useLocalStorage: localStorageAccessible(),
    });
  }

  private get enabled(): boolean {
    return window.ENV.SNOWPLOW_ENABLED || false;
  }

  private get initialized(): boolean {
    return !!this.tracker;
  }

  private logEvent(event: Event) {
    if (window.ENV.SNOWPLOW_DEBUG) {
      console.info("Emitting Snowplow Event:", event);
    }
  }
}
